<template>
  <e-sidebar
    :show="showSidebar"
    :fetching="loading"
    :title="$t('Detalhes da receita')"
    width="850px"
    :no-close-on-backdrop="false"
    @hidden="hide"
  >
    <template #content>
      <b-card-actions
        :title="$t('Dados')"
        no-actions
      >
        <b-row>
          <b-col md="2">
            <FormulateInput
              id="acc_pay_sidebar_info-receivable_id"
              v-model="data.receivable.id"
              type="label"
              :label="$t('Id do recebível')"
            />
          </b-col>
          <b-col md="2">
            <FormulateInput
              id="acc_pay_sidebar_info-id"
              v-model="data.id"
              type="label"
              :label="$t('Id da parcela')"
            />
          </b-col>
          <b-col md="4">
            <FormulateInput
              id="acc_pay_sidebar_info-store"
              v-model="data.receivable.store"
              type="label"
              filter="storeName"
              :label="$t('Loja')"
            />
          </b-col>
          <b-col md="4">
            <FormulateInput
              id="acc_pay_sidebar_info-document"
              :value="getDocument"
              type="label"
              :label="$t('Nº documento')"
            />
          </b-col>

          <b-col md="6">
            <FormulateInput
              id="acc_pay_sidebar_info-description"
              v-model="data.receivable.description"
              type="label"
              :label="$t('Descrição')"
            />
          </b-col>
          <b-col md="6">
            <FormulateInput
              id="acc_pay_sidebar_info-comment"
              :value="getComments"
              type="label"
              :label="$t('Comentário')"
            />
          </b-col>

          <b-col md="3">
            <FormulateInput
              id="acc_pay_sidebar_info-statement_date"
              v-model="data.receivable.issueDate"
              type="label"
              filter="date"
              :label="$t('Data da emissão')"
            />
          </b-col>
          <b-col md="3">
            <FormulateInput
              id="acc_pay_sidebar_info-due_date"
              v-model="data.dueDate"
              type="label"
              filter="date"
              :label="$t('Data do vencimento')"
            />
          </b-col>
          <b-col :md="hasSale ? '4' : '6'">
            <FormulateInput
              id="acc_pay_sidebar_info-checking_account"
              :value="data.receivable.managementAccount.name"
              type="label"
              :label="$t('Conta gerencial')"
            />
          </b-col>
          <b-col
            v-if="hasSale"
            md="2"
          >
            <FormulateInput
              id="acc_pay_sidebar_info-sale_id"
              :value="data.receivable.sale.id"
              type="label"
              :label="$t('Venda')"
              router-name="order-read-only"
              :router-params="{ id: data.receivable.sale.id }"
              :router-title="$t('Visualizar venda')"
            />
          </b-col>

          <b-col md="3">
            <FormulateInput
              id="acc_pay_sidebar_info-value"
              :value="data.value"
              type="label"
              filter="currency"
              :label="$t('Valor')"
            />
          </b-col>
          <b-col
            v-if="data.receivable.originalGrossValue"
            md="3"
          >
            <FormulateInput
              id="acc_pay_sidebar_info-value_original"
              :value="data.receivable.originalGrossValue"
              type="label"
              filter="currency"
              :label="$t('Valor Original')"
            />
          </b-col>
          <b-col md="3">
            <FormulateInput
              id="acc_pay_sidebar_info-payment_method"
              :value="getPaymentMethodName"
              type="label"
              :label="$t('Forma de pagamento')"
            />
          </b-col>
          <b-col md="6">
            <FormulateInput
              v-if="data.receivable.customer"
              id="acc_pay_sidebar_info-customer"
              :value="data.receivable.customer"
              type="label"
              filter="personName"
              :label="$t('Cliente')"
            />
          </b-col>

          <b-col md="4">
            <FormulateInput
              id="acc_pay_sidebar_info-created_by"
              :value="getCreatedBy"
              type="label"
              :label="$t('Criado por')"
            />
          </b-col>
          <b-col
            v-if="getModifiedBy"
            md="4"
          >
            <FormulateInput
              id="acc_pay_sidebar_info-modified_by"
              :value="getModifiedBy"
              type="label"
              :label="$t('Alterado por')"
            />
          </b-col>

          <b-col
            v-if="data.receivable.receivableOriginId"
            md="3"
          >
            <p class="h6 m-0">
              {{ $t('Id Receita de origem') }}
            </p>
            <p
              id="acc_pay_sidebar_info-receivable_origin_id"
              class="m-0 hyperlink cursor-pointer"
              @click="onSearchReceivableOrigin"
            >
              {{ data.receivable.receivableOriginId }}
            </p>
          </b-col>
        </b-row>
      </b-card-actions>

      <!--
        Parcelamento e custos da adquirente. Só aparece quando há o que mostrar: recebível à
        vista e sem conciliação continua com a tela de antes.
      -->
      <b-card-actions
        v-if="hasInstallmentInfo"
        :title="$t('Parcelamento e custos')"
        no-actions
      >
        <b-row>
          <b-col
            v-if="isCardInstallment"
            md="3"
          >
            <FormulateInput
              id="acc_pay_sidebar_info-installment_position"
              :value="installmentPositionLabel"
              type="label"
              :label="$t('Parcela')"
            />
          </b-col>
          <b-col
            v-if="installmentModeLabel"
            md="4"
          >
            <FormulateInput
              id="acc_pay_sidebar_info-installment_mode"
              :value="installmentModeLabel"
              type="label"
              :label="$t('Modalidade')"
            />
          </b-col>
          <b-col
            v-if="paymentSurchargeValue > 0"
            md="3"
          >
            <FormulateInput
              id="acc_pay_sidebar_info-payment_surcharge"
              :value="paymentSurchargeValue"
              type="label"
              filter="currency"
              :label="$t('Acréscimo do pagamento')"
            />
          </b-col>
        </b-row>

        <!--
          Previsto e realizado lado a lado. O previsto é congelado no registro do pagamento; o
          realizado só existe depois da conciliação, e por isso as duas colunas raramente estão
          preenchidas ao mesmo tempo.
        -->
        <b-row v-if="hasEstimatedCost">
          <b-col md="12">
            <p class="h6 mb-0 mt-1">
              {{ $t('Custo previsto') }}
            </p>
          </b-col>
          <b-col md="3">
            <FormulateInput
              id="acc_pay_sidebar_info-estimated_operation_cost"
              :value="data.estimatedOperationCost"
              type="label"
              filter="currency"
              :label="$t('Operação')"
            />
          </b-col>
          <b-col md="3">
            <FormulateInput
              id="acc_pay_sidebar_info-estimated_anticipation_cost"
              :value="data.estimatedAnticipationCost"
              type="label"
              filter="currency"
              :label="$t('Antecipação')"
            />
          </b-col>
          <b-col md="3">
            <FormulateInput
              id="acc_pay_sidebar_info-estimated_total_cost"
              :value="estimatedTotalCost"
              type="label"
              filter="currency"
              :label="$t('Total previsto')"
            />
          </b-col>
        </b-row>

        <b-row v-if="hasRealizedCost">
          <b-col md="12">
            <p class="h6 mb-0 mt-1">
              {{ $t('Custo realizado') }}
            </p>
          </b-col>
          <b-col
            v-if="data.realizedOperationCost != null"
            md="3"
          >
            <FormulateInput
              id="acc_pay_sidebar_info-realized_operation_cost"
              :value="data.realizedOperationCost"
              type="label"
              filter="currency"
              :label="$t('Operação')"
            />
          </b-col>
          <b-col
            v-if="data.realizedAnticipationCost != null"
            md="3"
          >
            <FormulateInput
              id="acc_pay_sidebar_info-realized_anticipation_cost"
              :value="data.realizedAnticipationCost"
              type="label"
              filter="currency"
              :label="$t('Antecipação')"
            />
          </b-col>
          <b-col md="3">
            <FormulateInput
              id="acc_pay_sidebar_info-realized_total_cost"
              :value="realizedTotalCost"
              type="label"
              filter="currency"
              :label="$t('Total realizado')"
            />
          </b-col>
        </b-row>
      </b-card-actions>

      <b-card-actions
        v-if="getPaidBy"
        :title="$t('Detalhes da baixa')"
        no-actions
      >
        <b-row>
          <b-col
            v-if="hasWriteOffDetails"
            md="3"
          >
            <FormulateInput
              id="acc_pay_sidebar_info-payment_date"
              v-model="data.paymentDate"
              type="label"
              filter="date"
              :label="$t('Data do pagamento')"
            />
          </b-col>
          <b-col
            v-if="hasWriteOffDetails"
            md="3"
          >
            <FormulateInput
              id="acc_pay_sidebar_info-checking_account"
              :value="data.checkingAccount.name"
              type="label"
              :label="$t('Conta corrente')"
            />
          </b-col>
          <b-col
            v-if="hasWriteOffDetails"
            md="3"
          >
            <FormulateInput
              id="acc_pay_sidebar_info-paid_value"
              :value="data.paidValue"
              type="label"
              filter="currency"
              :label="$t('Valor Recebido')"
            />
          </b-col>
          <b-col
            v-if="hasWriteOffDetails"
            md="3"
          >
            <FormulateInput
              id="acc_pay_sidebar_info-comission_value"
              :value="data.commissionValue"
              type="label"
              filter="currency"
              :label="$t('Valor da Taxa')"
            />
          </b-col>

          <b-col
            v-if="hasWriteOffDetails"
            md="3"
          >
            <FormulateInput
              id="acc_pay_sidebar_info-financial_write_off_payment_method"
              :value="data.financialWriteOffPaymentMethod.name"
              type="label"
              :label="$t('Forma de pagamento')"
            />
          </b-col>

          <b-col
            v-if="getPaidBy"
            md="5"
          >
            <FormulateInput
              id="acc_pay_sidebar_info-paid_by"
              :value="getPaidBy"
              type="label"
              :label="
                data.financialWriteOffIsActive ? $t('Baixado por') : $t('Baixa estornada por')
              "
            />
          </b-col>
          <b-col
            v-if="hasWriteOffDetails && getPaidBy"
            md="4"
          >
            <FormulateInput
              id="acc_pay_sidebar_info-financialWriteOffMode"
              :value="$t(`FinancialWriteOffMode_${data.financialWriteOffMode}`)"
              type="label"
              :label="$t('Forma da baixa')"
            />
          </b-col>
        </b-row>
      </b-card-actions>
    </template>

    <template #sidebar-footer>
      <e-button
        variant="primary"
        icon="x"
        :text="$t('Fechar')"
        :busy="saving"
        @click="hide"
      />
      <!-- <e-button
        v-if="showReverse"
        class="ml-1"
        :busy="saving"
        variant="danger"
        icon="x-circle"
        :text="$t('Estornar')"
        @click="localReverse"
      /> -->
    </template>
  </e-sidebar>
</template>

<script>
import { BRow, BCol } from 'bootstrap-vue'
import { alerts, financialDomain } from '@/mixins'
import BCardActions from '@/@core/components/b-card-actions/BCardActions.vue'
import ESidebar from '@/views/components/ESidebar.vue'
import EButton from '@/views/components/EButton.vue'

const getInitialSidebarData = () => ({
  id: null,
  store: null,
  user: null,
  description: null,
  value: null,
  paymentDate: null,
  paymentMethod: { name: null },
  receivable: {
    customer: null,
    managementAccount: { name: null },
    salePayment: { paymentMethodInterface: null },
  },
  financialWriteOffPaymentMethod: { name: null },
  checkingAccount: { name: null },
})

export default {
  components: {
    BRow,
    BCol,
    BCardActions,
    ESidebar,
    EButton,
  },

  mixins: [alerts, financialDomain],

  props: {},

  data() {
    return {
      showSidebar: false,
      loading: false,
      saving: false,
      data: getInitialSidebarData(),
    }
  },

  computed: {
    getCreatedBy() {
      if (!this.data.createdDate) return null
      return `${this.data.createdBy} (${this.$options.filters.date(this.data.createdDate)})`
    },
    getModifiedBy() {
      if (!this.data.modifiedDate) return null
      return `${this.data.modifiedBy} (${this.$options.filters.date(this.data.modifiedDate)})`
    },
    getPaidBy() {
      if (!this.data.writtenOffDate) return null
      return `${this.data.writtenOffBy} (${this.$options.filters.date(this.data.writtenOffDate)})`
    },
    getComments() {
      return [this.data.receivable.comments, this.data.comments].filter(Boolean).join(' - ')
    },
    getDocument() {
      if (!this.data.receivable.document) return null
      return `${this.data.receivable.document}${
        this.data.receivable.documentType ? ` (${this.data.receivable.documentType})` : ''
      }`
    },

    getPaymentMethodName() {
      if (this.data.receivable.salePayment?.paymentMethodInterface)
        return `${this.data.paymentMethod.name} (${this.data.receivable.salePayment?.paymentMethodInterface})`

      return this.data.paymentMethod.name
    },

    hasWriteOffDetails() {
      return this.data.paymentDate != null
    },

    hasSale() {
      return this.data.receivable.sale?.id
    },

    salePayment() {
      return this.data.receivable?.salePayment || {}
    },

    /**
     * Um cartão parcelado gera UM recebível com N parcelas — a transação na adquirente é uma só
     * e tem um só NSU. A posição está na parcela; o total, no pagamento.
     */
    isCardInstallment() {
      return (
        Number(this.salePayment.installmentCount) > 1 && Number(this.data.installment) > 0
      )
    },

    installmentPositionLabel() {
      if (!this.isCardInstallment) return null

      // Template literal, e não interpolação do $t: neste projeto o $t só substitui
      // placeholders em chaves de tradução registradas.
      return `${Number(this.data.installment)} ${this.$t('de')} ${Number(
        this.salePayment.installmentCount
      )}`
    },

    installmentModeLabel() {
      const labels = {
        Cash: this.$t('À vista'),
        Store: this.$t('Parcelado sem juros'),
        Issuer: this.$t('Parcelado com juros'),
      }

      return labels[this.salePayment.installmentMode] || null
    },

    paymentSurchargeValue() {
      return Number(this.salePayment.surchargeValue) || 0
    },

    hasEstimatedCost() {
      return (
        this.data.estimatedOperationCost != null || this.data.estimatedAnticipationCost != null
      )
    },

    estimatedTotalCost() {
      if (!this.hasEstimatedCost) return null

      return (
        (Number(this.data.estimatedOperationCost) || 0) +
        (Number(this.data.estimatedAnticipationCost) || 0)
      )
    },

    /**
     * O realizado só existe depois da conciliação. O total vem do commissionValue, que a
     * adquirente sempre informa; a decomposição em operação e antecipação depende do arquivo e
     * por isso é opcional.
     */
    hasRealizedCost() {
      return (
        this.data.commissionValue != null ||
        this.data.realizedOperationCost != null ||
        this.data.realizedAnticipationCost != null
      )
    },

    realizedTotalCost() {
      if (!this.hasRealizedCost) return null
      if (this.data.commissionValue != null) return Number(this.data.commissionValue)

      return (
        (Number(this.data.realizedOperationCost) || 0) +
        (Number(this.data.realizedAnticipationCost) || 0)
      )
    },

    hasInstallmentInfo() {
      return this.isCardInstallment || this.hasEstimatedCost || this.hasRealizedCost
    },

    showReverse() {
      return this.$can('Reverse', 'FinancialWriteOff')
    },
  },

  methods: {
    async show(existingData) {
      this.showSidebar = true
      this.loading = true

      try {
        if (existingData) {
          const defaultData = getInitialSidebarData()
          const { receivable } = defaultData
          this.data = {
            ...existingData,
            receivable: {
              ...existingData.receivable,
              managementAccount:
                existingData.receivable.managementAccount || receivable.managementAccount,
              customer: existingData.receivable.customer || receivable.customer,
              salePayment: existingData.receivable.salePayment || receivable.salePayment,
            },
            checkingAccount: existingData.checkingAccount || defaultData.checkingAccount,
            financialWriteOffPaymentMethod:
              existingData.financialWriteOffPaymentMethod ||
              defaultData.financialWriteOffPaymentMethod,
          }
        } else {
          // TODO ter uma busca por id da parcela?
          // const { data } = await this.$http.get(
          //   `/api/accounting/accounts-payable-installment/${installmentId}`
          // )
          // this.data = data
        }
      } catch (error) {
        this.showError({ error })
        this.hide()
      } finally {
        this.loading = false
      }
    },

    async localReverse() {
      await this.reverseWriteOff()
    },

    async reverseWriteOff() {
      const { accountsPayableInstalment } = this.data
      let writeOffId = null
      let writeOffMessage = ''

      writeOffId = accountsPayableInstalment[0]?.financialWriteOffId
      writeOffMessage = this.$t(
        'A receita será cancelada e o lançamento na conta corrente será excluído.'
      )

      const confirmed = await this.confirm({
        text: writeOffMessage,
      })
      if (!confirmed) return

      try {
        this.saving = true
        await this.$http.patch(`/api/financial/write-offs/${writeOffId}/reverse-payment`)
        this.showSuccess({ message: this.$t('Estornado com sucesso') })
        this.$emit('after-reverse')
        this.hide()
      } catch (error) {
        this.showError({ error })
      } finally {
        this.saving = false
      }
    },

    onSearchReceivableOrigin() {
      this.$emit('search-receivable-origin', this.data.receivable.receivableOriginId)
      this.hide()
    },

    cleanData() {
      this.data = getInitialSidebarData()
    },
    hide() {
      this.cleanData()
      this.showSidebar = false
    },
  },
}
</script>

<style lang="scss" scoped></style>
